import { baseApi } from "@/services/api";
import type {
  AddRotationTrackArgs,
  Channel,
  ChannelArtworkUploadUrlArgs,
  ChannelArtworkUploadUrlResult,
  ChannelPlaybackNextArgs,
  ChannelPlaybackNextResult,
  ChannelProgramming,
  CreateChannelArgs,
  CreateRotationArgs,
  ListChannelsResponse,
  MoveRotationTrackArgs,
  ScheduleUpdateArgs,
  SetChannelMusicDirectorsArgs,
  UpdateChannelArgs,
  UpdateRotationArgs,
} from "./types";

interface ApiEnvelope<T> {
  success: boolean;
  data?: T;
  message?: string;
  details?: unknown;
}

function unwrap<T>(raw: ApiEnvelope<T>, fallbackMessage: string): T {
  if (!raw || raw.success !== true || raw.data === undefined) {
    throw new Error(raw?.message || fallbackMessage);
  }
  return raw.data;
}

export const channelsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    listChannels: builder.query<ListChannelsResponse, void>({
      query: () => ({
        url: "/channels",
        method: "GET",
      }),
      transformResponse: (raw: ApiEnvelope<ListChannelsResponse>) =>
        unwrap(raw, "Failed to load channels"),
      providesTags: (result) =>
        result?.items
          ? [
              ...result.items.map((c) => ({ type: "Channel" as const, id: c.channelId })),
              { type: "Channel" as const, id: "LIST" },
            ]
          : [{ type: "Channel" as const, id: "LIST" }],
    }),

    getChannel: builder.query<Channel, { channelId: string }>({
      query: ({ channelId }) => ({
        url: `/channels/${encodeURIComponent(channelId)}`,
        method: "GET",
      }),
      transformResponse: (raw: ApiEnvelope<{ channel: Channel }>) => {
        const data = unwrap(raw, "Failed to load channel");
        if (!data.channel) throw new Error("Failed to load channel");
        return data.channel;
      },
      providesTags: (_result, _error, { channelId }) => [
        { type: "Channel" as const, id: channelId },
      ],
    }),

    createChannel: builder.mutation<Channel, CreateChannelArgs>({
      query: (body) => ({
        url: "/channels",
        method: "POST",
        body,
      }),
      transformResponse: (raw: ApiEnvelope<{ channel: Channel }>) => {
        const data = unwrap(raw, "Failed to create channel");
        if (!data.channel) throw new Error("Failed to create channel");
        return data.channel;
      },
      invalidatesTags: [{ type: "Channel", id: "LIST" }],
    }),

    updateChannel: builder.mutation<Channel, UpdateChannelArgs>({
      query: ({ channelId, ...patch }) => ({
        url: `/channels/${encodeURIComponent(channelId)}`,
        method: "PATCH",
        body: patch,
      }),
      transformResponse: (raw: ApiEnvelope<{ channel: Channel }>) => {
        const data = unwrap(raw, "Failed to update channel");
        if (!data.channel) throw new Error("Failed to update channel");
        return data.channel;
      },
      invalidatesTags: (_result, _error, { channelId }) => [
        { type: "Channel", id: "LIST" },
        { type: "Channel", id: channelId },
        { type: "ChannelProgramming", id: channelId },
      ],
    }),

    deleteChannel: builder.mutation<{ success: true }, { channelId: string }>({
      query: ({ channelId }) => ({
        url: `/channels/${encodeURIComponent(channelId)}`,
        method: "DELETE",
      }),
      transformResponse: (raw: ApiEnvelope<unknown>) => {
        if (!raw || raw.success !== true) {
          throw new Error(raw?.message || "Failed to delete channel");
        }
        return { success: true as const };
      },
      invalidatesTags: (_result, _error, { channelId }) => [
        { type: "Channel", id: "LIST" },
        { type: "Channel", id: channelId },
        { type: "ChannelProgramming", id: channelId },
      ],
    }),

    setChannelMusicDirectors: builder.mutation<Channel, SetChannelMusicDirectorsArgs>({
      query: ({ channelId, musicDirectors }) => ({
        url: `/channels/${encodeURIComponent(channelId)}/music-directors`,
        method: "PUT",
        body: { musicDirectors },
      }),
      transformResponse: (raw: ApiEnvelope<{ channel: Channel }>) => {
        const data = unwrap(raw, "Failed to set music directors");
        if (!data.channel) throw new Error("Failed to set music directors");
        return data.channel;
      },
      invalidatesTags: (_result, _error, { channelId }) => [
        { type: "Channel", id: "LIST" },
        { type: "Channel", id: channelId },
      ],
    }),

    getChannelArtworkUploadUrl: builder.mutation<
      ChannelArtworkUploadUrlResult,
      ChannelArtworkUploadUrlArgs
    >({
      query: ({ channelId, contentType }) => ({
        url: `/channels/${encodeURIComponent(channelId)}/artwork-upload-url`,
        method: "POST",
        body: { contentType },
      }),
      transformResponse: (raw: ApiEnvelope<ChannelArtworkUploadUrlResult>) =>
        unwrap(raw, "Failed to get channel artwork upload URL"),
    }),

    getChannelProgramming: builder.query<ChannelProgramming, { channelId: string }>({
      query: ({ channelId }) => ({
        url: `/channels/${encodeURIComponent(channelId)}/programming`,
        method: "GET",
      }),
      transformResponse: (raw: ApiEnvelope<ChannelProgramming>) =>
        unwrap(raw, "Failed to load channel programming"),
      providesTags: (_result, _error, { channelId }) => [
        { type: "ChannelProgramming" as const, id: channelId },
        { type: "Channel" as const, id: channelId },
      ],
    }),

    channelPlaybackNext: builder.mutation<
      ChannelPlaybackNextResult,
      ChannelPlaybackNextArgs
    >({
      query: ({ channelId, ...body }) => ({
        url: `/channels/${encodeURIComponent(channelId)}/playback/next`,
        method: "POST",
        body,
      }),
      transformResponse: (raw: ApiEnvelope<ChannelPlaybackNextResult>) =>
        unwrap(raw, "Failed to get next channel track"),
    }),

    createChannelRotation: builder.mutation<unknown, CreateRotationArgs>({
      query: ({ channelId, ...body }) => ({
        url: `/channels/${encodeURIComponent(channelId)}/rotations`,
        method: "POST",
        body,
      }),
      transformResponse: (raw: ApiEnvelope<unknown>) => {
        if (!raw || raw.success !== true) {
          throw new Error(raw?.message || "Failed to create rotation");
        }
        return raw.data ?? { success: true };
      },
      invalidatesTags: (_result, _error, { channelId }) => [
        { type: "ChannelProgramming", id: channelId },
        { type: "Channel", id: "LIST" },
        { type: "Channel", id: channelId },
      ],
    }),

    updateChannelRotation: builder.mutation<unknown, UpdateRotationArgs>({
      query: ({ channelId, rotationId, ...body }) => ({
        url: `/channels/${encodeURIComponent(channelId)}/rotations/${encodeURIComponent(rotationId)}`,
        method: "PATCH",
        body,
      }),
      transformResponse: (raw: ApiEnvelope<unknown>) => {
        if (!raw || raw.success !== true) {
          throw new Error(raw?.message || "Failed to update rotation");
        }
        return raw.data ?? { success: true };
      },
      invalidatesTags: (_result, _error, { channelId }) => [
        { type: "ChannelProgramming", id: channelId },
        { type: "Channel", id: "LIST" },
      ],
    }),

    deleteChannelRotation: builder.mutation<
      { success: true },
      { channelId: string; rotationId: string }
    >({
      query: ({ channelId, rotationId }) => ({
        url: `/channels/${encodeURIComponent(channelId)}/rotations/${encodeURIComponent(rotationId)}`,
        method: "DELETE",
      }),
      transformResponse: (raw: ApiEnvelope<unknown>) => {
        if (!raw || raw.success !== true) {
          throw new Error(raw?.message || "Failed to delete rotation");
        }
        return { success: true as const };
      },
      invalidatesTags: (_result, _error, { channelId }) => [
        { type: "ChannelProgramming", id: channelId },
        { type: "Channel", id: "LIST" },
        { type: "Channel", id: channelId },
      ],
    }),

    addChannelRotationTrack: builder.mutation<unknown, AddRotationTrackArgs>({
      query: ({ channelId, rotationId, ...body }) => ({
        url: `/channels/${encodeURIComponent(channelId)}/rotations/${encodeURIComponent(rotationId)}/tracks`,
        method: "POST",
        body,
      }),
      transformResponse: (raw: ApiEnvelope<unknown>) => {
        if (!raw || raw.success !== true) {
          throw new Error(raw?.message || "Failed to add track to rotation");
        }
        return raw.data ?? { success: true };
      },
      invalidatesTags: (_result, _error, { channelId }) => [
        { type: "ChannelProgramming", id: channelId },
        { type: "Channel", id: "LIST" },
      ],
    }),

    removeChannelRotationTrack: builder.mutation<
      { success: true },
      { channelId: string; rotationId: string; rotationTrackId: string }
    >({
      query: ({ channelId, rotationId, rotationTrackId }) => ({
        url: `/channels/${encodeURIComponent(channelId)}/rotations/${encodeURIComponent(rotationId)}/tracks/${encodeURIComponent(rotationTrackId)}`,
        method: "DELETE",
      }),
      transformResponse: (raw: ApiEnvelope<unknown>) => {
        if (!raw || raw.success !== true) {
          throw new Error(raw?.message || "Failed to remove track from rotation");
        }
        return { success: true as const };
      },
      invalidatesTags: (_result, _error, { channelId }) => [
        { type: "ChannelProgramming", id: channelId },
        { type: "Channel", id: "LIST" },
      ],
    }),

    moveChannelRotationTrack: builder.mutation<unknown, MoveRotationTrackArgs>({
      query: ({ channelId, rotationId, rotationTrackId, toRotationId }) => ({
        url: `/channels/${encodeURIComponent(channelId)}/rotations/${encodeURIComponent(rotationId)}/tracks/${encodeURIComponent(rotationTrackId)}/move`,
        method: "POST",
        body: { toRotationId },
      }),
      transformResponse: (raw: ApiEnvelope<unknown>) => {
        if (!raw || raw.success !== true) {
          throw new Error(raw?.message || "Failed to move track");
        }
        return raw.data ?? { success: true };
      },
      invalidatesTags: (_result, _error, { channelId }) => [
        { type: "ChannelProgramming", id: channelId },
        { type: "Channel", id: "LIST" },
      ],
    }),

    updateChannelSchedule: builder.mutation<unknown, ScheduleUpdateArgs>({
      query: ({ channelId, rotationSet }) => ({
        url: `/channels/${encodeURIComponent(channelId)}/schedule/update`,
        method: "POST",
        body: { rotationSet },
      }),
      transformResponse: (raw: ApiEnvelope<unknown>) => {
        if (!raw || raw.success !== true) {
          throw new Error(raw?.message || "Failed to update schedule");
        }
        return raw.data ?? { success: true };
      },
      invalidatesTags: (_result, _error, { channelId }) => [
        { type: "ChannelProgramming", id: channelId },
        { type: "Channel", id: "LIST" },
        { type: "Channel", id: channelId },
      ],
    }),

    cancelChannelSchedule: builder.mutation<
      { success: true },
      { channelId: string }
    >({
      query: ({ channelId }) => ({
        url: `/channels/${encodeURIComponent(channelId)}/schedule/cancel`,
        method: "POST",
      }),
      transformResponse: (raw: ApiEnvelope<unknown>) => {
        if (!raw || raw.success !== true) {
          throw new Error(raw?.message || "Failed to cancel pending schedule");
        }
        return { success: true as const };
      },
      invalidatesTags: (_result, _error, { channelId }) => [
        { type: "ChannelProgramming", id: channelId },
        { type: "Channel", id: "LIST" },
        { type: "Channel", id: channelId },
      ],
    }),

    goLiveChannelSchedule: builder.mutation<
      { success: true },
      { channelId: string }
    >({
      query: ({ channelId }) => ({
        url: `/channels/${encodeURIComponent(channelId)}/schedule/go-live`,
        method: "POST",
      }),
      transformResponse: (raw: ApiEnvelope<unknown>) => {
        if (!raw || raw.success !== true) {
          throw new Error(raw?.message || "Failed to go live");
        }
        return { success: true as const };
      },
      invalidatesTags: (_result, _error, { channelId }) => [
        { type: "ChannelProgramming", id: channelId },
        { type: "Channel", id: "LIST" },
        { type: "Channel", id: channelId },
      ],
    }),
  }),
});

export function extractChannelErrorMessage(err: unknown): string {
  if (!err) return "Request failed";
  if (typeof err === "string") return err;
  if (typeof err === "object") {
    const anyErr = err as Record<string, unknown>;
    const data = anyErr.data as Record<string, unknown> | undefined;
    if (data && typeof data.message === "string" && data.message.length > 0) {
      return data.message;
    }
    if (typeof anyErr.message === "string" && anyErr.message.length > 0) {
      return anyErr.message;
    }
    if (typeof anyErr.error === "string" && anyErr.error.length > 0) {
      return anyErr.error;
    }
    if (typeof anyErr.status === "number") {
      return `Request failed (HTTP ${anyErr.status})`;
    }
  }
  return "Request failed";
}

export const {
  useListChannelsQuery,
  useGetChannelQuery,
  useCreateChannelMutation,
  useUpdateChannelMutation,
  useDeleteChannelMutation,
  useSetChannelMusicDirectorsMutation,
  useGetChannelArtworkUploadUrlMutation,
  useGetChannelProgrammingQuery,
  useChannelPlaybackNextMutation,
  useCreateChannelRotationMutation,
  useUpdateChannelRotationMutation,
  useDeleteChannelRotationMutation,
  useAddChannelRotationTrackMutation,
  useRemoveChannelRotationTrackMutation,
  useMoveChannelRotationTrackMutation,
  useUpdateChannelScheduleMutation,
  useCancelChannelScheduleMutation,
  useGoLiveChannelScheduleMutation,
} = channelsApi;
